import { differenceInSeconds } from "date-fns"
import { motion } from "motion/react"
import type React from "react"
import type { ComponentPropsWithoutRef } from "react"
import { classNames } from "../../utils"

type Variant = "table" | "grid"

type LiveAnimationProps = {
  itemKey: string
  children: React.ReactNode
  variant: Variant
  liveAddedAt?: Date
  ref?: React.Ref<HTMLDivElement>
}

export function LiveAnimation({
  children,
  itemKey,
  liveAddedAt,
  variant,
  ref,
}: LiveAnimationProps) {
  // This is a workaround so that we never show the animation again when the component is re-rendered in
  // e.g. virtualized lists
  const isLive = liveAddedAt && differenceInSeconds(new Date(), liveAddedAt) < 1

  if (variant === "table") {
    return (
      <div
        className={classNames(
          isLive &&
            "slide-in-from-top animate-in duration-200 ease-out-quint *:animate-live-animation-bg",
        )}
      >
        {children}
      </div>
    )
  }

  const animationProperties: Record<
    "grid",
    Partial<ComponentPropsWithoutRef<typeof motion.div>>
  > = {
    grid: {
      animate: {
        opacity: 1,
        transition: {
          delay: 0.2,
        },
      },
      exit: {
        opacity: 0,
        transition: { duration: 0.1 },
      },
      initial: isLive ? { opacity: 0 } : undefined,
    },
  }

  return (
    <motion.div
      className={isLive ? "*:animate-live-animation-bg" : undefined}
      key={itemKey}
      layout
      ref={ref}
      transition={{ duration: 0.1, bounce: 0, type: "spring" }}
      {...animationProperties[variant]}
    >
      {children}
    </motion.div>
  )
}
